import {
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Table,
    TableContainer,
    Tag,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
    useDisclosure,
} from '@chakra-ui/react'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { fetchWrapper, getUserTimeZone } from '../../helpers'
import {
    LuBell,
    LuCheckCircle,
    LuClock,
    LuExternalLink,
    LuFileText,
    LuMail,
    LuMessageCircle,
    LuMessageSquare,
    LuPhone,
    LuSmartphone,
    LuXCircle,
} from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import Pagination from 'react-js-pagination'
import Prism from 'prismjs'
import 'prismjs/components/prism-json'
import 'prismjs/themes/prism-tomorrow.css'

const Channels = [
    { name: 'Whatsapp', icon: LuMessageCircle, color: '#00a63e', id: 2 },
    { name: 'SMS', icon: LuMessageSquare, color: '#155dfc', id: 1 },
    { name: 'E-Mail', icon: LuMail, color: '#9810fa', id: 3 },
    { name: 'Push Notification', icon: LuBell, color: '#e60076', id: 6 },
    { name: 'In-App Notification', icon: LuBell, color: '#4f39f6', id: 5 },
    { name: 'Voice (IVR)', icon: LuPhone, color: '#f54a00', id: 4 },
];

const getChannelInfo = (channelIdOrName) => {
    return (
        Channels.find((c) => c.id === channelIdOrName || c.name === channelIdOrName) ||
        { name: 'System', icon: LuSmartphone, color: 'gray' }
    );
};

const getStatusColor = (statusName) => {
    const name = statusName?.toLowerCase() || '';
    if (name.includes('read') || name.includes('delivered')) return 'green';
    if (name.includes('sent')) return 'blue';
    if (name.includes('fail') || name.includes('undeliverable')) return 'red';
    if (name.includes('accept')) return 'green';
    return 'gray';
};

const getTypeId = (typeId) => {
    switch (typeId) {
        case 1: return 'Authentication';
        case 2: return 'Transactional';
        case 3: return 'Marketing';
        case 4: return 'Informational';
        default: return 'Unknown';
    }
};

const regexCheckHtml = /<[a-zA-Z][^>]*>/;

const MessageLog = ({ id }) => {
    const [listLog, setListLog] = useState([])
    const [statusList, setStatusList] = useState([]);
    const [activePage, setActivePage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const [selectedMessage, setSelectedMessage] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedPayload, setSelectedPayload] = useState(null);
    const { isOpen: isPayloadOpen, onOpen: onPayloadOpen, onClose: onPayloadClose } = useDisclosure();

    const handleNavigate = (history) => {
        let encoded = btoa(JSON.stringify(history))
        localStorage.setItem("data", encoded)
        navigate(`/report`)
    }

    const onChangePaginate = (pageNumber) => {
        setActivePage(pageNumber);
    };

    useEffect(() => {
        loadLog(activePage);
        loadStatusList();
    }, [activePage, id]);

    const loadLog = async (page = 1) => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/msg-log/list?customer_id=${id}&page=${page}&limit=${itemsPerPage}`;
            const data = await fetchWrapper.get(url);
            setListLog(data);
        } catch (error) {
            console.error("Error loading logs:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadStatusList = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/status/list`;
            const data = await fetchWrapper.get(url);
            setStatusList(data?.data || data || []);
        } catch (error) {
            console.error("Error loading status list:", error);
        }
    };

    const getStatusDetails = (statusId) => {
        return statusList.find((s) => s.status_id === statusId || s.id === statusId);
    };

    const openModal = (rs) => {
        setSelectedMessage(rs);
        setIsOpen(true);
    };

    const closeModal = () => {
        setIsOpen(false);
        setSelectedMessage(null);
    };

    const handleViewPayload = (payload) => {
        const parsedPayload = JSON.parse(JSON.stringify(payload, (key, value) => {
            if (typeof value === "string" && value.startsWith("{") && value.endsWith("}")) {
                try {
                    return JSON.parse(value);
                } catch {
                    return value;
                }
            }
            return value;
        }));

        const formatted = JSON.stringify(parsedPayload, null, 2);
        setSelectedPayload(formatted);
        onPayloadOpen();

        setTimeout(() => Prism.highlightAll(), 0);
    };

    return (
        <div className="table_section">
            {loading && <Flex justifyContent="center" py={4}>Loading...</Flex>}

            <TableContainer width="100%">
                <Table variant='simple' width="100%">
                    <Thead>
                        <Tr>
                            <Th>Message ID</Th>
                            <Th>Recipient</Th>
                            <Th>Message</Th>
                            <Th>Channel</Th>
                            <Th>Count</Th>
                            <Th>Cost</Th>
                            <Th>Date & Time</Th>
                            <Th>Status</Th>
                            <Th></Th>
                        </Tr>
                    </Thead>

                    <Tbody fontSize="14px">
                        {listLog?.data?.map((rs, index) => {
                            const channel = Channels.find((c) => c.id === rs.MsgChannel?.id) || Channels.find((c) => c.name === rs.MsgChannel?.name);
                            const IconComp = channel?.icon || LuSmartphone;
                            const channelColor = channel?.color || 'gray';

                            return (
                                <Tr key={rs?.id || index}>
                                    <Td
                                        whiteSpace="nowrap"
                                        color="#2563eb"
                                        cursor="pointer"
                                        onClick={() => openModal(rs)}
                                        _hover={{ textDecoration: 'underline' }}
                                    >
                                        <HStack spacing={1}>
                                            <Text>{rs.message_id}</Text>
                                            <Icon as={LuExternalLink} boxSize={3} />
                                        </HStack>
                                    </Td>

                                    <Td style={{ textWrap: "wrap" }}>{rs.sent_to || '—'}</Td>

                                    <Td
                                        maxW="200px"
                                        whiteSpace="nowrap"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        title={regexCheckHtml.test(rs.message) ? null : rs.message}
                                    >
                                        {regexCheckHtml.test(rs.message) ? (
                                            <Popover placement="auto" closeOnBlur>
                                                <PopoverTrigger>
                                                    <Text color="#2563eb" cursor="pointer" isTruncated>
                                                        This message contains HTML. Click to preview.
                                                    </Text>
                                                </PopoverTrigger>

                                                <PopoverContent w="max-content" maxW="90vw">
                                                    <PopoverArrow />
                                                    <PopoverCloseButton />
                                                    <PopoverHeader fontWeight="semibold">HTML Preview</PopoverHeader>
                                                    <PopoverBody>
                                                        <Box maxH="400px" overflowY="auto" borderRadius="md">
                                                            <iframe
                                                                srcDoc={rs.message}
                                                                title="HTML Preview"
                                                                style={{
                                                                    width: "100%",
                                                                    height: "auto",
                                                                    border: "none",
                                                                    borderRadius: "6px",
                                                                    background: "white",
                                                                }}
                                                                sandbox="allow-same-origin"
                                                            />
                                                        </Box>
                                                    </PopoverBody>
                                                </PopoverContent>
                                            </Popover>
                                        ) : (
                                            rs.message || "—"
                                        )}
                                    </Td>

                                    <Td>
                                        <HStack spacing={2}>
                                            <Icon as={IconComp} color={channelColor} boxSize={4} />
                                            <Text fontSize="sm" fontWeight="thin">
                                                {rs.MsgChannel?.name === 'In-App Notification' ? 'In-App' : rs.MsgChannel?.name || 'N/A'}
                                            </Text>
                                        </HStack>
                                    </Td>

                                    <Td>{rs?.message_count ?? 1}</Td>

                                    <Td fontSize="xs" whiteSpace="nowrap">
                                        <Text>Cost: {rs?.price ?? '-'}</Text>
                                        <Text>Saving: {rs?.saving ?? '-'}</Text>
                                        <Text>Decision Charges: {rs?.api_charges ?? '-'}</Text>
                                    </Td>

                                    <Td whiteSpace="nowrap" verticalAlign="top">
                                        <VStack align="flex-start" spacing={0}>
                                            <Text color="#64748b" fontSize="xs">
                                                Received: {rs.receive_date ? moment.utc(rs.receive_date).tz(getUserTimeZone()).format('DD MMM YY hh:mm:ss A') : '-'}
                                            </Text>
                                            <Text color="#64748b" fontSize="xs">
                                                Sent: {rs.sent_date ? moment.utc(rs.sent_date).tz(getUserTimeZone()).format('DD MMM YY hh:mm:ss A') : '-'}
                                            </Text>
                                        </VStack>
                                    </Td>

                                    <Td whiteSpace="nowrap">
                                        <HStack
                                            spacing={1.5}
                                            display="inline-flex"
                                            alignItems="center"
                                            px={2.5}
                                            py={1}
                                            borderRadius="full"
                                            border="1px solid"
                                            borderColor={`${rs?.Status?.color || 'gray'}.200`}
                                            bg={`${rs?.Status?.color || 'gray'}.50`}
                                        >
                                            <Box
                                                w="6px"
                                                h="6px"
                                                borderRadius="full"
                                                bg={`${rs?.Status?.color || 'gray'}.500`}
                                                flexShrink={0}
                                            />
                                            <Text
                                                fontSize="xs"
                                                color={`${rs?.Status?.color || 'gray'}.700`}
                                                fontWeight="500"
                                                whiteSpace="nowrap"
                                            >
                                                {rs?.Status?.name || 'Unknown'}
                                            </Text>
                                        </HStack>
                                    </Td>

                                    <Td>
                                        <Icon
                                            as={LuFileText}
                                            onClick={() => handleNavigate(rs)}
                                            cursor="pointer"
                                            boxSize={4}
                                            color="#2563eb"
                                            title="View report"
                                        />
                                    </Td>
                                </Tr>
                            )
                        })}
                    </Tbody>
                </Table>
            </TableContainer>

            <Flex justifyContent="center" alignItems="center" my={4}>
                <Pagination
                    activePage={activePage}
                    itemsCountPerPage={itemsPerPage}
                    totalItemsCount={listLog.count || 0}
                    pageRangeDisplayed={5}
                    onChange={onChangePaginate}
                    innerClass="pagination"
                    itemClass="page-item"
                    activeLinkClass="page-link active"
                    linkClass="page-link"
                    prevPageText="Previous"
                    nextPageText="Next"
                />
            </Flex>

            {/* Message Delivery Journey */}
            <Modal isOpen={isOpen} onClose={closeModal} size="xl" isCentered>
                <ModalOverlay />
                <ModalContent maxW="600px">
                    <ModalHeader borderBottom="1px" borderColor="gray.200" pb={3} fontSize="lg" fontWeight="semibold">
                        <HStack spacing={2}>
                            <Icon as={LuClock} color="blue.500" />
                            <Text>Message Delivery Journey</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />

                    <ModalBody py={4}>
                        {selectedMessage ? (
                            <VStack align="stretch" spacing={4}>
                                <Box bg="gray.50" borderRadius="md">
                                    <Box bg="red.50" boxShadow="sm" borderRadius="lg" border={1} borderColor="red.200" borderStyle="solid" mb={2}>
                                        <Text fontSize="xs" color="black" p={2}>
                                            <Flex justifyContent={"space-between"} alignItems={"center"}>
                                                <strong>Message Id: {selectedMessage.messageid || selectedMessage.message_id || selectedMessage.id}</strong>
                                                {selectedMessage.customer_payload && (
                                                    <Button size={"xs"} bg={"#2563ebe6"} color={"white"} onClick={() => handleViewPayload(selectedMessage.customer_payload)}>
                                                        View Payload
                                                    </Button>
                                                )}
                                            </Flex>
                                        </Text>
                                    </Box>
                                    <Text fontSize="sm" color="gray.600">
                                        Complete delivery timeline showing channel-specific status progression and auto-failover attempts.
                                    </Text>
                                </Box>

                                <Box bg="blue.50" border="1px" borderColor="blue.200" borderRadius="md" p={3}>
                                    <Flex justify="space-between" mb={2}>
                                        <Box flex="1">
                                            <Text fontSize="xs" color="gray.600" mb={1}>Recipient</Text>
                                            <Text fontSize="xs" fontWeight="semibold">{selectedMessage.sent_to || selectedMessage.to || '-'}</Text>
                                        </Box>
                                        <Box flex="1">
                                            <Text fontSize="xs" color="gray.600" mb={1}>Message Type</Text>
                                            <Tag fontSize="xs" fontWeight="normal" border={"1px"} borderColor="gray.300" color={"black"} bg="white">
                                                {getTypeId(selectedMessage.message_type_id)}
                                            </Tag>
                                        </Box>
                                        <Box flex="1">
                                            <Text fontSize="xs" color="gray.600" mb={1}>Final Status</Text>
                                            <Tag colorScheme={getStatusColor(selectedMessage?.Status?.name)} px={2} py={1} borderRadius="md">
                                                {selectedMessage?.Status?.name || 'Unknown'}
                                            </Tag>
                                        </Box>
                                    </Flex>
                                    <Box>
                                        {selectedMessage.message && (
                                            <>
                                                <Text fontSize="xs" color="gray.600" mb={2} fontWeight="medium">Message Content</Text>
                                                <Box bg="white" border={1} borderColor={"gray.100"} p={3} borderRadius="md">
                                                    {regexCheckHtml.test(selectedMessage.message) ? (
                                                        <Box maxH="400px" overflowY="auto" borderRadius="md">
                                                            <iframe
                                                                srcDoc={selectedMessage.message}
                                                                title="HTML Message"
                                                                style={{ width: "100%", height: "auto", border: "none", borderRadius: "6px" }}
                                                                sandbox="allow-same-origin"
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <Text fontSize="sm">{selectedMessage.message}</Text>
                                                    )}
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                </Box>

                                <Box>
                                    <HStack spacing={2} mb={3}>
                                        <Icon as={LuClock} color="blue.500" boxSize={4} />
                                        <Text fontSize="sm" fontWeight="semibold">Delivery Timeline & Status Progression</Text>
                                    </HStack>

                                    <VStack align="stretch" spacing={0} pl={2}>
                                        {selectedMessage.history?.map((h, i) => {
                                            const status = getStatusDetails(h.status_id);
                                            const { name: channelName, icon: ChannelIcon, color: channelColor } = getChannelInfo(h.channel_id);

                                            const isFailed = status?.name?.toLowerCase().includes("fail") ||
                                                status?.name?.toLowerCase().includes("undeliverable");

                                            const currentIndex = selectedMessage.history.findIndex((item) => item === h);
                                            const nextStatus = selectedMessage.history[currentIndex + 1];
                                            const nextStatusDetails = nextStatus ? getStatusDetails(nextStatus.status_id) : null;

                                            const isNextNotFailed =
                                                nextStatusDetails &&
                                                !nextStatusDetails?.name?.toLowerCase().includes("fail") &&
                                                !nextStatusDetails?.name?.toLowerCase().includes("undeliverable");

                                            const shouldTriggerFailover = isFailed && isNextNotFailed;

                                            const getColoredStatusBg = (status) => {
                                                if (!status) return "white";
                                                const name = status.name?.toLowerCase() || "";
                                                if (name.includes("read") || name.includes("delivered") || name.includes("clicked")) return "#f0fdf4";
                                                if (name.includes("sent")) return "#eff6ff";
                                                if (name.includes("fail") || name.includes("undeliverable") || name.includes("invalid")) return "#fef2f2";
                                                return "white";
                                            }
                                            const getColoredStatusBorder = (status) => {
                                                if (!status) return "gray";
                                                const name = status.name?.toLowerCase() || "";
                                                if (name.includes("read") || name.includes("delivered") || name.includes("clicked")) return "#00c951";
                                                if (name.includes("sent")) return "#2b7fff";
                                                if (name.includes("fail") || name.includes("undeliverable") || name.includes("invalid")) return "#fb2c36";
                                                if (name.includes("accept")) return "#6a7282";
                                                return "gray";
                                            }

                                            return (
                                                <Flex key={i}>
                                                    <Box
                                                        flex="1"
                                                        bg={getColoredStatusBg(status)}
                                                        border="1px"
                                                        borderColor={getColoredStatusBg(status)}
                                                        borderLeftWidth="4px"
                                                        borderLeftColor={getColoredStatusBorder(status)}
                                                        borderRadius="md"
                                                        my={3}
                                                        px={2}
                                                        py={5}
                                                    >
                                                        <Flex align="start" gap={3}>
                                                            {status?.msg_status === "1" || status?.msg_status === 1 ? (
                                                                <Icon as={LuCheckCircle} color="#00a63e" boxSize={4} mt="2px" />
                                                            ) : (
                                                                <Icon as={LuXCircle} color="#e7000b" boxSize={4} mt="2px" />
                                                            )}

                                                            <Box flex="1">
                                                                <Flex justify="space-between" align="start" mb={1}>
                                                                    <HStack spacing={2}>
                                                                        {h.channel_id ? (
                                                                            <HStack spacing={1}>
                                                                                <Icon as={ChannelIcon} color={channelColor} boxSize={3.5} />
                                                                                <Text fontSize="xs" fontWeight="semibold">{channelName}</Text>
                                                                            </HStack>
                                                                        ) : (
                                                                            <Text fontSize="xs" fontWeight="semibold">{channelName}</Text>
                                                                        )}
                                                                        {(status?.msg_status === "0" || status?.msg_status === 0) && (
                                                                            <Tag bg={"red"} size={"sm"} color={"white"}>Failed</Tag>
                                                                        )}
                                                                        <Tag
                                                                            size="sm"
                                                                            border="1px solid"
                                                                            borderColor="gray.300"
                                                                            color={status?.name?.toLowerCase().includes("accept") ? "black" : "#64748b"}
                                                                            bg="transparent"
                                                                        >
                                                                            {status?.name || `Status ${h.status_id}`}
                                                                        </Tag>
                                                                    </HStack>

                                                                    <Text fontSize="xs" color="gray.500">
                                                                        {h.time ? new Date(h.time).toLocaleString("en-PK", { dateStyle: "short", timeStyle: "medium" }) : '-'}
                                                                    </Text>
                                                                </Flex>
                                                                {(h.channel_id === 5 || h.channel_id === 6) && h.subscriber_id && (
                                                                    <Text fontSize="xs" color="gray.600">Subscriber Id: {h.subscriber_id}</Text>
                                                                )}
                                                                <Text fontSize="xs" color="gray.600">
                                                                    {status?.description ||
                                                                        (isFailed
                                                                            ? "Message Undeliverable - Customer not on platform"
                                                                            : status?.name === "Message Accepted"
                                                                                ? "Message Accepted - Request received and validated"
                                                                                : status?.name === "Message Sent"
                                                                                    ? `Message Sent to ${channelName}`
                                                                                    : status?.name === "Message Delivered"
                                                                                        ? "Message Delivered"
                                                                                        : status?.name === "Message Read"
                                                                                            ? "Message Read"
                                                                                            : "")}
                                                                </Text>

                                                                {shouldTriggerFailover && (
                                                                    <Text fontSize="xs" color="#ca3500" bg="#ffedd4" px={2} py={1} mt={2} borderRadius="md" fontWeight="medium">
                                                                        → Triggering auto-failover to next channel
                                                                    </Text>
                                                                )}
                                                            </Box>
                                                        </Flex>
                                                    </Box>
                                                </Flex>
                                            );
                                        })}
                                    </VStack>
                                </Box>
                            </VStack>
                        ) : (
                            <Text>No details available.</Text>
                        )}
                    </ModalBody>

                    <ModalFooter borderTop="1px" borderColor="gray.200">
                        <Button onClick={closeModal} size={"sm"} bg="#2563eb" color="white">
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* View Payload */}
            <Modal isOpen={isPayloadOpen} onClose={onPayloadClose} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Payload Details</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {selectedPayload ? (
                            <Box maxH="400px" overflowY="auto" color="green.200" borderRadius="md">
                                <pre style={{ backgroundColor: "black", textShadow: "none", color: "white", padding: "0px", borderRadius: "8px", fontFamily: "monospace" }}>
                                    <span style={{ padding: 15, backgroundColor: "#2F2F2F", fontSize: "14px", display: "block", marginBottom: "10px" }}>
                                        json
                                    </span>
                                    <code
                                        className="language-json"
                                        style={{ padding: "0px 15px 15px", display: "block", whiteSpace: "pre-wrap" }}
                                        dangerouslySetInnerHTML={{ __html: Prism.highlight(selectedPayload, Prism.languages.json, 'json') }}
                                    />
                                </pre>
                            </Box>
                        ) : (
                            <Text>No payload found.</Text>
                        )}

                        {(() => {
                            const raw = selectedMessage?.error_log;
                            if (!raw || (Array.isArray(raw) && raw.length === 0)) return null;

                            let errorLogs = raw;
                            if (typeof raw === 'string') {
                                try { errorLogs = JSON.parse(raw); } catch { errorLogs = null; }
                            }

                            return (
                                <Box mt={4}>
                                    <Text fontWeight="semibold" fontSize="sm" mb={2} color="red.700">Error Log</Text>
                                    {Array.isArray(errorLogs) ? (
                                        errorLogs.map((err, i) => (
                                            <Box key={i} bg="red.50" border="1px" borderColor="red.200" borderRadius="md" p={3} mb={2}>
                                                <HStack spacing={2} mb={1}>
                                                    <Text fontSize="xs" fontWeight="bold" color="red.700">{err.title}</Text>
                                                    <Text fontSize="xs" color="gray.500">(Code: {err.code})</Text>
                                                </HStack>
                                                {err.error_data?.details && (
                                                    <Text fontSize="xs" color="gray.700">{err.error_data.details}</Text>
                                                )}
                                                {err.href && (
                                                    <Text fontSize="xs" color="blue.500" mt={1} as="a" href={err.href} target="_blank" rel="noreferrer">
                                                        {err.href}
                                                    </Text>
                                                )}
                                            </Box>
                                        ))
                                    ) : (
                                        <Box bg="red.50" border="1px" borderColor="red.200" borderRadius="md" p={3}>
                                            <Text fontSize="xs" color="red.700" whiteSpace="pre-wrap">{typeof raw === 'string' ? raw : JSON.stringify(raw)}</Text>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })()}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onPayloadClose} size={"sm"} bg="#2563eb" color="white">Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    )
}

export default MessageLog
